import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CountryProps } from '../../pages/Cannabis';

const GROUNDING_STEPS = [
  { icon: '👀', label: '5 things you can SEE', desc: 'Look around. Name 5 things you can see right now. A wall, a lamp, your hands, a window, a shoe.' },
  { icon: '✋', label: '4 things you can TOUCH', desc: 'Feel 4 textures. The fabric of your clothes, the surface you\'re sitting on, your phone, your hair.' },
  { icon: '👂', label: '3 things you can HEAR', desc: 'Listen carefully. Traffic, a fan humming, your own breathing, music, birds outside.' },
  { icon: '👃', label: '2 things you can SMELL', desc: 'Sniff around. Food cooking, soap on your hands, fresh air, the room you\'re in.' },
  { icon: '👅', label: '1 thing you can TASTE', desc: 'What can you taste? Drink some water. Taste your lip balm. Chew some gum.' },
];

const TIPS = [
  { title: 'Drink water', desc: 'Hydration helps. Sip cold water slowly. It also helps with dry mouth.' },
  { title: 'Eat something', desc: 'Food can help reduce the intensity. Snack on something familiar and comforting.' },
  { title: 'Smell black pepper', desc: 'Seriously — sniffing or chewing black peppercorns activates CB2 receptors and can reduce THC-induced anxiety. Neil Young\'s famous tip.' },
  { title: 'Chew on a lemon peel', desc: 'Limonene (the terpene in citrus) has anti-anxiety effects that can counteract THC.' },
  { title: 'Take CBD', desc: 'CBD can counteract THC\'s psychoactive effects. If you have CBD oil or gummies, take some.' },
  { title: 'Change your environment', desc: 'Move to a different room. Step outside for fresh air. A change of scenery can shift your mindset.' },
  { title: 'Put on familiar music', desc: 'Something you know well and love. Familiar music is grounding and comforting.' },
  { title: 'Call someone you trust', desc: 'Talk to a friend. Just hearing a familiar voice can be incredibly calming.' },
  { title: 'Take a shower', desc: 'Warm water is soothing and can help reset your state. Focus on the sensation of water.' },
  { title: 'Remember: this will pass', desc: 'No one has ever died from cannabis. The peak lasts 15-30 minutes for smoking, 1-2 hours for edibles. You WILL feel normal again.' },
];

function BreathingExercise() {
  const [phase, setPhase] = useState<'in' | 'hold' | 'out' | 'rest'>('in');
  const [count, setCount] = useState(4);
  const [active, setActive] = useState(false);
  const [cycles, setCycles] = useState(0);

  const tick = useCallback(() => {
    setCount(prev => {
      if (prev <= 1) {
        setPhase(p => {
          if (p === 'in') return 'hold';
          if (p === 'hold') return 'out';
          if (p === 'out') { setCycles(c => c + 1); return 'rest'; }
          return 'in';
        });
        return phase === 'out' ? 2 : phase === 'rest' ? 2 : 4;
      }
      return prev - 1;
    });
  }, [phase]);

  useEffect(() => {
    if (!active) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [active, tick]);

  const phaseLabel = { in: 'Breathe In', hold: 'Hold', out: 'Breathe Out', rest: 'Rest' }[phase];
  const phaseColor = { in: '#4ade80', hold: '#d4a574', out: '#60a5fa', rest: '#a78bfa' }[phase];
  const ringSize = phase === 'in' ? 'scale-110' : phase === 'out' ? 'scale-90' : 'scale-100';

  return (
    <div className="flex flex-col items-center space-y-6 py-4">
      <motion.div
        animate={{ scale: phase === 'in' ? 1.3 : phase === 'out' ? 0.8 : 1 }}
        transition={{ duration: phase === 'rest' ? 0.5 : 3, ease: 'easeInOut' }}
        className="w-32 h-32 rounded-full border-4 flex items-center justify-center"
        style={{ borderColor: phaseColor }}
      >
        <div className="text-center">
          <p className="text-2xl font-bold text-white">{active ? count : '--'}</p>
          <p className="text-xs font-semibold" style={{ color: phaseColor }}>{active ? phaseLabel : 'Ready'}</p>
        </div>
      </motion.div>
      <button
        onClick={() => { setActive(!active); setPhase('in'); setCount(4); setCycles(0); }}
        className={`px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
          active ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-[#4ade80]/20 text-[#4ade80] border border-[#4ade80]/30'
        }`}
      >
        {active ? 'Stop' : 'Start Breathing Exercise'}
      </button>
      {cycles > 0 && <p className="text-white/30 text-xs">{cycles} cycle{cycles > 1 ? 's' : ''} completed</p>}
      <p className="text-white/30 text-xs text-center max-w-xs">4-4-4 box breathing. Inhale 4 seconds, hold 4 seconds, exhale 4 seconds. Used by Navy SEALs for anxiety control.</p>
    </div>
  );
}

export default function PanicButtonTab(_props: CountryProps) {
  const [showGrounding, setShowGrounding] = useState(false);
  const [groundingStep, setGroundingStep] = useState(0);
  const [showBreathing, setShowBreathing] = useState(false);

  return (
    <div className="space-y-6 max-w-xl mx-auto">
      {/* Hero */}
      <div className="text-center space-y-3 py-4">
        <h2 className="text-2xl font-bold text-white">You're Okay.</h2>
        <p className="text-white/50 text-sm leading-relaxed max-w-md mx-auto">
          This feeling is temporary. Cannabis cannot cause a fatal overdose. You are safe. Let's get you through this.
        </p>
      </div>

      {/* Quick reminders */}
      <div className="rounded-2xl border border-[#4ade80]/20 bg-[#4ade80]/5 p-5 space-y-3">
        <h3 className="text-[#4ade80] font-semibold text-sm">Quick Reminders</h3>
        <ul className="text-white/60 text-xs space-y-2 leading-relaxed">
          <li>- Nobody has ever died from cannabis alone. Ever.</li>
          <li>- Smoking peaks in 15-30 minutes, then fades.</li>
          <li>- Edibles peak in 1-2 hours, then slowly fade over 4-6 hours.</li>
          <li>- Your heart rate may feel fast — this is normal and temporary.</li>
          <li>- Paranoia and anxiety are common THC side effects — they will pass.</li>
        </ul>
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          onClick={() => { setShowBreathing(!showBreathing); setShowGrounding(false); }}
          className={`p-4 rounded-2xl border text-left transition-colors ${
            showBreathing ? 'bg-[#60a5fa]/10 border-[#60a5fa]/30' : 'bg-white/[0.02] border-white/10 hover:border-white/20'
          }`}
        >
          <p className="text-white font-semibold text-sm">Breathing Exercise</p>
          <p className="text-white/40 text-xs mt-1">Guided 4-4-4 box breathing to slow your heart rate</p>
        </button>
        <button
          onClick={() => { setShowGrounding(!showGrounding); setShowBreathing(false); setGroundingStep(0); }}
          className={`p-4 rounded-2xl border text-left transition-colors ${
            showGrounding ? 'bg-[#a78bfa]/10 border-[#a78bfa]/30' : 'bg-white/[0.02] border-white/10 hover:border-white/20'
          }`}
        >
          <p className="text-white font-semibold text-sm">5-4-3-2-1 Grounding</p>
          <p className="text-white/40 text-xs mt-1">Sensory grounding technique to anchor you to reality</p>
        </button>
      </div>

      {/* Breathing */}
      <AnimatePresence>
        {showBreathing && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="rounded-2xl border border-[#60a5fa]/20 bg-[#60a5fa]/5 p-5 overflow-hidden">
            <BreathingExercise />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grounding */}
      <AnimatePresence>
        {showGrounding && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            className="rounded-2xl border border-[#a78bfa]/20 bg-[#a78bfa]/5 p-5 space-y-4 overflow-hidden">
            <div className="text-center">
              <p className="text-4xl mb-2">{GROUNDING_STEPS[groundingStep].icon}</p>
              <h3 className="text-white font-bold text-lg">{GROUNDING_STEPS[groundingStep].label}</h3>
              <p className="text-white/50 text-sm mt-2">{GROUNDING_STEPS[groundingStep].desc}</p>
            </div>
            <div className="flex justify-center gap-2">
              {GROUNDING_STEPS.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === groundingStep ? 'bg-[#a78bfa]' : i < groundingStep ? 'bg-[#a78bfa]/40' : 'bg-white/10'}`} />
              ))}
            </div>
            <div className="flex justify-center gap-3">
              {groundingStep > 0 && (
                <button onClick={() => setGroundingStep(s => s - 1)} className="px-4 py-2 rounded-xl border border-white/10 text-white/50 text-xs">Back</button>
              )}
              <button
                onClick={() => setGroundingStep(s => Math.min(s + 1, GROUNDING_STEPS.length - 1))}
                className="px-4 py-2 rounded-xl bg-[#a78bfa]/20 border border-[#a78bfa]/30 text-[#a78bfa] text-xs font-semibold"
              >
                {groundingStep === GROUNDING_STEPS.length - 1 ? 'Done! You did great.' : 'Next Step'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tips */}
      <div className="space-y-3">
        <h3 className="text-white/40 text-xs font-semibold uppercase tracking-wider">Things That Help</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {TIPS.map((tip, i) => (
            <div key={i} className="rounded-xl border border-white/5 bg-white/[0.02] p-3 space-y-1">
              <p className="text-white font-semibold text-xs">{tip.title}</p>
              <p className="text-white/40 text-[11px] leading-relaxed">{tip.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
